import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { createContext, useContext } from 'react';

// Define the API base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

// Define the auth context
interface AuthContextType {
  token: string | null;
}

export const AuthContext = createContext<AuthContextType>({ token: null });

// Create the axios instance
const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Request interceptor for adding the auth token
axiosInstance.interceptors.request.use(
  (config) => {
    // Try to get token from localStorage first
    let token = localStorage.getItem('auth_token');
    
    // If no token in localStorage, try to get from context
    if (!token) {
      try {
        // This will only work within a React component or custom hook
        const authContext = useContext(AuthContext);
        token = authContext.token;
      } catch (error) {
        // Not being called within a React component or custom hook
        console.debug('Not in React component context, cannot access AuthContext');
      }
    }
    
    // If token exists, add it to the headers
    if (token && config.headers) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling common response scenarios
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error: AxiosError) => {
    if (error.response) {
      // Handle specific error status codes
      const status = error.response.status;
      
      if (status === 401) {
        // Unauthorized - clear token and redirect to login
        localStorage.removeItem('token');
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
      }
      
      if (status === 403) {
        // Forbidden - user doesn't have permission
              }
    } else if (error.request) {
      // The request was made but no response was received
          } else {
      // Something happened in setting up the request
          }
    
    return Promise.reject(error);
  }
);

// Create a hook for using the axios instance with the auth context
export const useAxios = () => {
  const authContext = useContext(AuthContext);
  
  // Create a function that will add the auth token from context
  const authorizedRequest = async (config: AxiosRequestConfig) => {
    if (authContext.token && config.headers) {
      config.headers['Authorization'] = `Bearer ${authContext.token}`;
    }
    return axiosInstance(config);
  };
  
  return { axiosInstance, authorizedRequest };
};

export default axiosInstance;